import React, {useState} from "react";
import {Button, Form, Input, message, Modal} from "antd";
import {HttpService} from "../../services/http/Http.service";
import {useProgressContext} from "../../services/ProgressContext";
import {SimulatorPersonIdPair} from "../../model/models";
import {AuthService} from "../../services/Auth.service";
import {HttpErrorService} from "../../services/http/HttpError.service";
import ConfirmModal from "../confirmModal/ConfirmModal";
import {EnvService} from "../../services/Env.service";

interface SignerFileProps {
    selectedSimulatorPersonPairs: SimulatorPersonIdPair[];
    onSuccess?: (signedBlob: Blob) => void;
    withConfirmationModal: boolean;
    buttonName: string | 'Firma';
    confirmationModalTitle: string | 'Trasferisci pratiche';
    confirmationModalMessage: string | 'Sei sicuro di voler trasferire le pratiche e scaricare tutti i documenti?';
}

const SignerFile: React.FC<SignerFileProps> = ({ selectedSimulatorPersonPairs,
                                                   onSuccess,
                                                   withConfirmationModal,
                                                   buttonName,
                                                   confirmationModalTitle,
                                                   confirmationModalMessage}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [form] = Form.useForm();
    const { startPolling } = useProgressContext();

    const startSigning = () => {
        if (selectedSimulatorPersonPairs.length > 0)
            HttpService.getInstanceAxios().get(`/auth/autenticazione-firma?t=${new Date().getTime()}`)
                .then(response => {
                    const authenticated = response.data || false;
                    if (!authenticated) {
                        const windowFeatures = "left=150,top=150,width=1280,height=768";
                        console.warn("Signing access token in missing, redirecting to authentication page")
                        let openedWindow = window.open(EnvService.getBackEndUrl() + "/auth/autentica-firma",
                            "Autenticazione Sessione Operatore",
                            windowFeatures);

                        if (openedWindow != null) {
                            const timer = setInterval(() => {
                                // @ts-ignore
                                if (openedWindow.closed) {
                                    clearInterval(timer);
                                    console.debug("Window closed")
                                    startAsyncSigning();
                                }
                            }, 500);
                        }
                    } else {
                        startAsyncSigning();
                    }
                });
        else
            message.error("Nessuna pratica selezionata!")
    };
    const startAsyncSigning = async () => {
        setIsLoading(true);
        if (selectedSimulatorPersonPairs.length === 0) {
            message.error("Nessuna pratica selezionata!");
            return;
        }

        try {

            const fileResponse = await HttpService.getInstanceAxios().post(
                `/template/generate-diffida`,
                selectedSimulatorPersonPairs,
                { responseType: "blob" }
            );

            const fileBlob = new Blob([fileResponse.data], { type: "application/pdf" });

            const formData = new FormData();
            formData.append("file", fileBlob);

            const startResponse = await HttpService.getInstanceAxios().post(
                `/signer/async-process/file`,
                formData
                // Headers object removed
            );

            const newSessionId = startResponse.data;
            setSessionId(newSessionId);

            message.success("File caricato nel sistema di firma");

            startPolling(newSessionId, () => {
                setIsModalOpen(true);
            });

        } catch (error) {
            setIsLoading(false)
            HttpErrorService.handle("Errore durante la generazione o il caricamento del file", error, true);
        }
        setIsLoading(false)
    };

    const onButtonClick = () => {
        if(withConfirmationModal)
            setIsConfirmationModalOpen(true)
        else
            startSigning()
    };

    const handleModalSubmit = async () => {
        try {
            setIsLoading(true)
            const values = await form.validateFields();

            const signResponse = await HttpService.getInstanceAxios().post(
                `/signer/async-process/file/${sessionId}`,
                {
                    otp: values.otp,
                    username: values.username,
                    password: values.password,
                    simulatorPersonIdPairs: selectedSimulatorPersonPairs,
                },
                { timeout: 30000, responseType: "blob" }
            );

            const signedBlob = new Blob([signResponse.data], { type: "application/pdf" });
            message.success("Firma completato");

            if(onSuccess)
                onSuccess(signedBlob);

            setIsModalOpen(false);
            setIsConfirmationModalOpen(false)
            form.resetFields();
        } catch (err) {
            setIsModalOpen(false);
            setIsConfirmationModalOpen(false)
            setIsLoading(false)
            HttpErrorService.handle("Errore durante la firma digitale", err, true);
        }
        setIsLoading(false)
    };

    return (
        <>
            {AuthService.isAdmin() && (
                <Button
                    className="custom-label-color"
                    onClick={onButtonClick}
                    size="large"
                    disabled={selectedSimulatorPersonPairs.length === 0}
                    type="primary"
                >
                    {buttonName}
                </Button>
            )}

            <ConfirmModal
                visible={isConfirmationModalOpen}
                onConfirm={startSigning}
                onCancel={() => setIsConfirmationModalOpen(false)}
                title={confirmationModalTitle}
                message={confirmationModalMessage}
                loading={isLoading}
                confirmText="Sì"
                cancelText="Annulla"
            />

            <Modal
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleModalSubmit}
                title="Firma Digitale"
                okText="Firma"
                confirmLoading={isLoading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Username"
                        name="username"
                        rules={[{ required: true, message: "Inserisci lo username" }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Password"
                        name="password"
                        rules={[{ required: true, message: "Inserisci la password" }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        label="OTP"
                        name="otp"
                        rules={[{ required: true, message: "Inserisci il codice OTP" }]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default SignerFile;
